/**
 * Modo Asesor v2 — modal "Añadir empresa".
 *
 * El despacho invita a una empresa-cliente por email. POST /api/asesor/invitations
 * crea la invitación y envía el correo; el dueño la acepta desde el enlace.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { apiFetch } from '@/utils/api';
import { qk } from '@/lib/qk';
import { toastApiError } from '@/lib/query-client';

export function AddClientCompanyModal({ open, onOpenChange, firmId }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const invite = useMutation({
    mutationFn: () =>
      apiFetch('/api/asesor/invitations', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), message: message.trim() || undefined }),
      }),
    onSuccess: () => {
      toast.success(`Invitación enviada a ${email.trim()}`);
      queryClient.invalidateQueries({ queryKey: qk.asesor.cartera(firmId) });
      setEmail('');
      setMessage('');
      onOpenChange(false);
    },
    onError: (err) => toastApiError(err, 'No se pudo enviar la invitación'),
  });

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Añadir empresa</DialogTitle>
          <DialogDescription>
            Invita a una empresa-cliente por email. Cuando su responsable acepte, quedará vinculada a tu despacho.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email del responsable</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="responsable@empresa.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-message">Mensaje (opcional)</Label>
            <Textarea
              id="invite-message"
              placeholder="Hola, te invito a gestionar la contabilidad de tu empresa en VERIX…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={invite.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => invite.mutate()} disabled={!emailValid || invite.isPending}>
            {invite.isPending ? 'Enviando…' : 'Enviar invitación'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddClientCompanyModal;
